import { Input, message, Select } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import BookBankImage from 'assets/images/driver-portal/preview/book-bank.png';
import { Button } from 'components';
import FormItem from 'components/Form/FormItem';
import Divider from 'containers/DriverPortalPage/components/Divider';
import DriverPortalLayout from 'containers/DriverPortalPage/components/DriverPortalLayout';
import HeaderPortal from 'containers/DriverPortalPage/MainPage/components/HeaderPortal';
import ReturnButton from 'containers/DriverPortalPage/MainPage/components/ReturnButton';
import UploadImageForm from 'containers/DriverPortalPage/MainPage/components/UploadImageForm';
import { FormikProps, FormikValues, useFormik } from 'formik';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import { useUpdateExpireDateImages } from 'hooks/useUpdateExpireDateImages';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addressService,
  authenticationService,
  documentReaderService,
  mainService,
  uploadService,
} from 'services/driverPortal';
import { STATUS_RESPONSE } from 'services/driverPortal/DriverPortal.interface';
import { getCurrentDate } from 'utils/dateUtils';
import { appendOcrMetadata } from 'utils/appendOcrMetadata';
import * as Yup from 'yup';
import Loading from '../../../../components/ReturnOrderModal/Loading';
import { ReaderType } from '../../../../services/driverPortal/documentReader/DocumentReaderService';
import { isPdfFile } from '../../../../utils/isFilePdf';
import { ASCII_DIGITS_ONLY } from '../../../../utils/validate';
import ConfirmReturnModal from '../components/ConfirmReturnModal';
import Information from '../components/Information';

export interface IBookBankPageProps {}

interface IBookBankImage {
  image: string;
  createdAt?: string;
}

interface IBookBankRequest {
  document: IBookBankImage;
  name: string;
  accountNumber: string;
  status?: string;
  accountName: string;
  accountType?: string;
  fileData?: any;
  error?: IError;
}

interface IError {
  bookBankError?: string;
}

interface IBookBankImageUpload {
  document?: FormData;
}

enum ImageTypes {
  DOCUMENT = 'document',
}

const imageKeys: ImageTypes[] = [ImageTypes.DOCUMENT];

const folderType = 'book-bank';

export const accountNumberRegExp = /^[0-9]{10}/;

const validateBookBank = Yup.object().shape({
  document: Yup.object().shape({
    image: Yup.string().nullable().required('กรุณาเลือกรูปภาพ'),
  }),
  name: Yup.string().nullable().required('กรุณาเลือกธนาคาร'),
  accountNumber: Yup.string()
    .matches(accountNumberRegExp, 'กรุณากรอกเลขบัญชีธนาคาร')
    .matches(ASCII_DIGITS_ONLY, 'ไม่สามารถกรอกอักษรอื่นๆได้นอกจากตัวเลข')
    .required('กรุณากรอกเลขบัญชีธนาคาร'),
});

const BookBankPage: React.FC<IBookBankPageProps> = () => {
  const { search, pushUrl } = useQueryParams();
  const { userId, accessToken } = authenticationService.getUserToken();
  const [disableEdit, setDisableEdit] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [imageDeletionFiles, setImageDeletionFiles] = useState<UploadFile[]>(
    [],
  );
  const { updateByFiles } = useUpdateExpireDateImages();
  const [, setShowPrompt] = usePrompt();
  const [formDirty, setFormDirty] = useState(false);
  const [accountOcrLoading, setAccountOcrLoading] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<IBookBankImageUpload>({});

  const [saveLoading, setSaveLoading] = React.useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [bookBankFileName, setBookBankFileName] = useState<string>('');

  const goToMainPage = () => {
    pushUrl('/', search);
  };

  const goToLoginPage = () => {
    pushUrl('/login', search);
  };

  const uploadImage = async (
    files: IBookBankImageUpload,
    key: ImageTypes,
    formValues: IBookBankRequest,
    createdAt: string,
  ) => {
    const uploadFile = files[key] || null;

    if (uploadFile) {
      const response = await uploadService.uploadFile(uploadFile);

      if (response.data) {
        formValues[key] = {
          image: response.data[0],
          createdAt,
        };
      }
    }
  };

  const buildPayloadBookBank = async (values: IBookBankRequest) => {
    const f = values.fileData ?? {};

    const fd = new FormData();
    fd.append('tab', 'book_bank');
    if (filesToUpload.document?.get('files')) {
      fd.append('bankImage', filesToUpload.document?.get('files') ?? '');
    }
    fd.append('accountNo', values.accountNumber ?? f.accountNumber);
    fd.append('accountName', values.accountName ?? f.accountName);
    fd.append('typesOfAccount', values.accountType ?? f.accountType);
    fd.append('bankName', values.name);
    appendOcrMetadata(fd, 'bookBankOcrMetadata', f);
    return fd;
  };

  // const submitBookBank = (
  //   formValues: IBookBankRequest,
  // ): Action<IBookBankResponse> => {
  //   return {
  //     method: 'PUT',
  //     endpoint,
  //     body: formValues,
  //     headers: {
  //       Authorization: accessToken,
  //     },
  //   };
  // };
  // const {
  //   payload: bookBank,
  //   error: getBookBankError,
  //   loading: getBookBankLoading,
  //   status: getBookBankStatus,
  // } = useQuery<IBookBankResponse>({
  //   method: 'GET',
  //   endpoint,
  //   headers: {
  //     Authorization: accessToken,
  //   },
  // });

  // const { mutate: mutationBookBank } = useMutation(submitBookBank);

  const formik = useFormik<IBookBankRequest>({
    initialValues: {
      error: undefined,
      document: {
        image: '',
        createdAt: '',
      },
      name: 'ธนาคารไทยพาณิชย์ จำกัด (มหาชน)',
      accountNumber: '',
      accountName: '',
      fileData: null,
    },
    validationSchema: validateBookBank,
    enableReinitialize: true,
    onSubmit: async (formValues, action) => {
      if (saveLoading) return;
      setSaveLoading(true);
      try {
        const createdAt = getCurrentDate();
        const payload = await buildPayloadBookBank(formValues);
        console.log('============payload', payload);
        if (payload instanceof FormData) {
          for (const [k, v] of payload.entries()) {
            console.log(
              k,
              v instanceof File ? `${v.name} (${v.type}, ${v.size}b)` : v,
            );
          }
        } else {
          console.log('payload', payload);
        }

        const res = await addressService.updateInfo(payload);
        console.log('=========success', res);

        const out = (res as any)?.data ?? res;

        if (out?.success === false) {
          const msg =
            out?.errorMessageList?.[0] || out?.message || 'บันทึกไม่สำเร็จ';
          message.error(msg);
          return;
        }

        message.success(out?.message || 'บันทึกข้อมูลเรียบร้อยแล้ว');
        goToMainPage();
      } catch (err) {
        const data = err?.response?.data;
        const status = err?.response?.status;
        console.error('API error status:', status);
        console.error('API error data:', data);
        const detail =
          data?.detail ||
          data?.message ||
          err?.message ||
          'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์';
        message.error('เลขที่บัญชีนี้มีอยู่แล้ว');
        formik.setErrors({ accountNumber: 'เลขที่บัญชีนี้มีอยู่แล้ว' });
        formik.setFieldValue('accountNumber', '');
        formik.setFieldValue('document', { image: '' });
        formik.setFieldTouched('document.image', true, false);
        formik.setFieldError('document.image', 'กรุณาเลือกรูปภาพ');
        // if (
        //   typeof detail === 'string' &&
        //   detail.includes('บัญชีนี้มีอยู่แล้ว')
        // ) {
        //   formik.setErrors({ accountNumber: 'เลขที่บัญชีนี้มีอยู่แล้ว' });
        //   message.error(detail);
        // }
      } finally {
        action?.setSubmitting?.(false);
        setSaveLoading(false);
      }
    },
  });

  const handleRemoveImage = useCallback(
    (file: UploadFile) => {
      formik.setFieldValue('fileData', undefined);

      // setDisableSubmit(false);
      // formik.setFieldValue('error', {
      //   driverLicenseError: undefined,
      // });

      if (!file.fileName?.match(/^blob\:/)) {
        setImageDeletionFiles([...imageDeletionFiles, file]);
      }
    },
    [imageDeletionFiles],
  );

  const clearBookBankImage = (msg: string) => {
    const url = formik.values.document?.image;

    if (url && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }

    handleRemoveImage({ fileName: url } as UploadFile);

    setFilesToUpload(prev => ({ ...prev, document: undefined }));
    setBookBankFileName('');
    formik.setFieldValue('document', { image: '' });
    formik.setFieldTouched('document.image', true, false);
    formik.setFieldError('document.image', msg);

    formik.setFieldValue('accountNumber', '');
  };

  const loadImage = async (path: string) => {
    try {
      const response = await mainService.loadImageAxios(path);
      return response;
    } catch (e) {
      return null;
    }
  };

  const handleSelect = (key: string, value: string) => {
    formik.setFieldValue(key, value);
  };

  useEffect(() => {
    const { dirty, isSubmitting } = formik;

    const isDirty = !isSubmitting && dirty;
    setFormDirty(isDirty);

    setIsShowModal(isDirty);

    setShowPrompt(isDirty);

    console.log('===== Debug formik data: ', formik.values);
  }, [formik]);

  useEffect(() => {
    const { accessToken } = authenticationService.getUserToken() || {};
    if (!accessToken) {
      pushUrl('/login', search);
    }
  }, [pushUrl, search]);

  const errors = formik.errors || {};

  const shouldDisplayErrorMessage = (key: keyof IBookBankRequest) => {
    return formik.touched[key] && formik.errors[key];
  };

  const handleUnauthorized = useCallback(async status => {
    if (status === STATUS_RESPONSE.UNAUTHORIZED) {
      await authenticationService.logout();
      goToLoginPage();
    }
  }, []);

  const getInfo = async () => {
    try {
      const response = await mainService.getInfo();

      console.log('===== Debug response: getInfo', response);
      // const response = mainService.getMockPotentialLead(id);
      if (response.data.potentialLeadBookBank) {
        const infoData = response.data.potentialLeadBookBank;
        setIsApproved(infoData.statusCode === 'approved');

        formik.setFieldValue('accountNumber', infoData.accountNo ?? '');
        formik.setFieldValue('accountName', infoData.accountName ?? '');
        if (infoData?.bankImage) {
          const image = await loadImage(infoData?.bankImage);
          setBookBankFileName(infoData.bankImage);
          formik.setFieldValue('fileData', {});
          formik.setFieldValue('document', {
            image: image ? image.url : undefined,
          });
          // formik.setFieldTouched('accountNumber', false, false);
          // formik.setFieldError('accountNumber', undefined);
          // formik.setFieldError('accountNumber', '');
        }
      }
    } catch (e) {
      console.log('===== Debug error status: ', e.toString());
    }
  };

  useEffect(() => {
    getInfo();
  }, []);

  // useEffect(() => {
  //   handleUnauthorized(getBookBankStatus);
  //
  //   if (!bookBank) return;
  //
  //   if (shouldDisableEdit(bookBank)) {
  //     setDisableEdit(true);
  //   }
  // }, [getBookBankStatus, bookBank]);

  // if (getBookBankError && getBookBankStatus !== STATUS_RESPONSE.UNAUTHORIZED)
  //   return <ErrorPage />;

  // if (getBookBankLoading) return <Loading />;

  const shouldDisabledSubmit = () => {
    return (
      disableEdit ||
      Boolean(!formik.values.document.image) ||
      !accountNumberRegExp.test(formik.values.accountNumber)
    );
  };

  const readerFile = async (file: File) => {
    try {
      if (file) {
        const res: any = await documentReaderService.readerFile(
          file,
          ReaderType.BOOK_BANK,
        );

        if (res.status) {
          const bankName = res.bank_name || res.bankname || '';
          if (
            bankName === 'ธนาคารไทยพาณิชย์ จำกัด (มหาชน)' ||
            'ธนาคารไทยพาณิชย์' ||
            bankName.toLowerCase().includes('siam commercial bank')
          ) {
            formik.setFieldValue('fileData', res);
            const normalized = String(res?.account_number ?? '')
              .replace(/\D/g, '')
              .slice(0, 10);
            formik.setFieldValue('accountName', res.account_holder_name);
            formik.setFieldValue('accountNumber', normalized);
            formik.setFieldTouched('accountNumber', true, false);

            formik.setFieldError('accountNumber', undefined);
            formik.setFieldError('document.image', undefined);
            formik.setFieldValue('accountType', res.account_type);
          } else {
            formik.setFieldValue('fileData', {});
            formik.setFieldValue('document', {
              image: undefined,
            });
            message.error(
              'ขออภัย ไม่สามารถอัปโหลดได้ ระบบรองรับสมุดบัญชีของธนาคารไทยพาณิชย์ เท่านั้น',
            );
          }
        } else {
          formik.setFieldValue('fileData', {});
          formik.setFieldTouched('document.image', true, false);
          formik.setFieldError(
            'document.image',
            'รูปภาพไม่ชัดเจนไม่สามารถอ่านได้',
          );
          formik.setFieldValue('error', {
            bookBankError:
              'ระบบไม่สามารถอัพโหลดรูปที่ไม่ใช่สมุดบัญชีของธนาคารไทยพาณิชย์ได้',
          });
          formik.setFieldValue('fileData', {});
          formik.setFieldValue('document', {
            image: undefined,
          });
        }
      }
    } catch (e) {
      console.log('===== Debug error status: ', e.toString());
      formik.setFieldValue('fileData', {});
      formik.setFieldValue('document', {
        image: undefined,
      });
    }
  };

  return (
    <>
      {formik.values.document.image && !formik.values.fileData && <Loading />}
      <form onSubmit={formik.handleSubmit}>
        <HeaderPortal formik={formik as FormikProps<FormikValues>} />
        <DriverPortalLayout>
          <ReturnButton />

          <Information
            title="สมุดบัญชีธนาคาร"
            description='กรุณากรอกข้อมูล และอัพโหลดเอกสารที่มีเครื่องหมาย * ให้ครบถ้วน แล้วกด"บันทึก"'
          />
          <Divider />
          <FormItem
            required
            labelCol={{ span: 24 }}
            colon={false}
            // validateStatus={errors?.document?.image ? 'error' : ''}
            // help={errors?.document?.image}
            validateStatus={
              errors?.document?.image || formik.values.error?.bookBankError
                ? 'error'
                : ''
            }
            help={formik.values.error?.bookBankError ?? errors?.document?.image}
          >
            <UploadImageForm
              title="อัพโหลดสมุดบัญชีธนาคารไทยพาณิชย์"
              required
              bulletList={[
                'ต้องเป็นบัญชีธนาคารไทยพาณิชย์เท่านั้น',
                'ชื่อในสมุดบัญชี ต้องตรงกับชื่อผู้สมัคร',
                'เห็นข้อมูลชัดเจนและครบถ้วน',
              ]}
              previewTitle="ตัวอย่างสมุดบัญชีธนาคาร"
              previewUrl={BookBankImage}
              onChange={value => {
                const files = value.uploadFileForm
                  ? (value.uploadFileForm.get('files') as Blob)
                  : '';

                setFilesToUpload({
                  ...filesToUpload,
                  document: value.uploadFileForm,
                });

                formik.setFieldValue('document', {
                  image: files ? URL.createObjectURL(files) : '',
                });

                if (value.uploadFileForm?.get('files')) {
                  // @ts-ignore
                  readerFile(value.uploadFileForm.get('files'));
                }
              }}
              disabled={disableEdit || isApproved}
              pathImage={formik.values.document.image}
              onRemove={handleRemoveImage}
              nameImage="สมุดบัญชีธนาคาร"
              folder={folderType}
              isPdf={isPdfFile(bookBankFileName)}
              fileName={bookBankFileName}
            />
          </FormItem>

          <FormItem
            label="ธนาคาร"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('name' as any) ? 'error' : ''
            }
            help={shouldDisplayErrorMessage('name' as any) && errors?.name}
          >
            <Select
              disabled={disableEdit || isApproved}
              size="large"
              showSearch
              placeholder="เลือกธนาคาร"
              optionFilterProp="children"
              options={[
                {
                  label: 'ธนาคารไทยพาณิชย์ จำกัด (มหาชน)',
                  value: 'ธนาคารไทยพาณิชย์ จำกัด (มหาชน)',
                },
              ]}
              value={formik?.values?.name}
              onBlur={formik.handleBlur}
              onChange={value => handleSelect('name', value)}
            />
          </FormItem>
          <FormItem
            label="เลขบัญชีธนาคาร"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={errors?.accountNumber ? 'error' : ''}
            help={errors?.accountNumber}
            style={{ marginBottom: '0px' }}
          >
            <Input
              disabled={disableEdit || isApproved}
              size="large"
              name="accountNumber"
              placeholder="ตัวอย่าง 1234567890123"
              maxLength={10}
              onBlur={formik.handleBlur}
              value={formik.values.accountNumber}
              onChange={e =>
                formik.setFieldValue(
                  'accountNumber',
                  e.target.value.replace(/[^0-9]/g, '').slice(0, 10),
                )
              }
              inputMode="tel"
              pattern="[0-9]*"
            />
          </FormItem>
          <div>
            เลขที่บัญชีธนาคาร 10 หลัก (ไม่ต้องเว้นวรรค ไม่ต้องใส่ขีดคั่น)
          </div>
          <Divider />

          <Button
            block
            type={shouldDisabledSubmit() || isApproved ? 'default' : 'primary'}
            size="large"
            disabled={shouldDisabledSubmit() || isApproved}
            htmlType="submit"
            loading={saveLoading}
          >
            บันทึก
          </Button>
        </DriverPortalLayout>

        {formDirty && <ConfirmReturnModal isShow={isShowModal} />}
      </form>
    </>
  );
};

export default BookBankPage;
